import { boolean, index, jsonb, pgTable, text, timestamp, uniqueIndex, uuid, } from "drizzle-orm/pg-core";
import { organisations } from "./organisations";
import { organisationIntegrations } from "./integrations";
// ── Calendar Sources (provider-agnostic) ─────────────────────────────
export const calendarSources = pgTable("calendar_sources", {
    id: uuid("id").primaryKey().defaultRandom(),
    integrationId: uuid("integration_id")
        .notNull()
        .references(() => organisationIntegrations.id, { onDelete: "cascade" }),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    provider: text("provider").notNull().$type(),
    calendarId: text("calendar_id").notNull(),
    summary: text("summary"),
    primary: boolean("primary").notNull().default(false),
    timezone: text("timezone"),
    selected: boolean("selected").notNull().default(false),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    uniqueCalendarPerIntegration: uniqueIndex("calendar_sources_integration_calendar").on(table.integrationId, table.calendarId),
    companyIdx: index("calendar_sources_company").on(table.companyId),
    providerIdx: index("calendar_sources_provider").on(table.companyId, table.provider),
}));
export const gcalWatchChannels = pgTable("gcal_watch_channels", {
    id: uuid("id").primaryKey().defaultRandom(),
    calendarDbId: uuid("calendar_db_id")
        .notNull()
        .references(() => calendarSources.id, { onDelete: "cascade" }),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    channelId: text("channel_id").notNull(),
    resourceId: text("resource_id").notNull(),
    tokenHash: text("token_hash").notNull(),
    expiration: timestamp("expiration", { withTimezone: true }).notNull(),
    status: text("status").notNull().$type().default("active"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    channelIdIdx: uniqueIndex("gcal_watch_channels_channel_id").on(table.channelId),
    calendarIdx: index("gcal_watch_channels_calendar").on(table.calendarDbId),
    expirationIdx: index("gcal_watch_channels_expiration").on(table.expiration),
}));
// ── GCal Sync State (provider-specific) ──────────────────────────────
export const gcalSyncState = pgTable("gcal_sync_state", {
    id: uuid("id").primaryKey().defaultRandom(),
    calendarDbId: uuid("calendar_db_id")
        .notNull()
        .references(() => calendarSources.id, { onDelete: "cascade" }),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    syncToken: text("sync_token"),
    lastSyncAt: timestamp("last_sync_at", { withTimezone: true }),
    lastFullSyncAt: timestamp("last_full_sync_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    uniqueCalendar: uniqueIndex("gcal_sync_state_calendar").on(table.calendarDbId),
    companyIdx: index("gcal_sync_state_company").on(table.companyId),
}));
export const calendarEvents = pgTable("calendar_events", {
    id: uuid("id").primaryKey().defaultRandom(),
    calendarDbId: uuid("calendar_db_id")
        .notNull()
        .references(() => calendarSources.id, { onDelete: "cascade" }),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    provider: text("provider").notNull().$type(),
    eventId: text("event_id").notNull(),
    status: text("status").notNull().$type().default("confirmed"),
    summary: text("summary"),
    description: text("description"),
    startTs: timestamp("start_ts", { withTimezone: true }),
    endTs: timestamp("end_ts", { withTimezone: true }),
    organiser: text("organiser"),
    attendeesJson: jsonb("attendees_json").$type().default([]),
    htmlLink: text("html_link"),
    location: text("location"),
    conferenceLink: text("conference_link"),
    userRsvpStatus: text("user_rsvp_status"),
    transparency: text("transparency"),
    recurringEventId: text("recurring_event_id"),
    rawJson: jsonb("raw_json").notNull().default({}),
    providerUpdatedAt: timestamp("provider_updated_at", { withTimezone: true }),
    lastSeenAt: timestamp("last_seen_at", { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    uniqueEventPerCalendar: uniqueIndex("calendar_events_calendar_event").on(table.calendarDbId, table.eventId),
    companyIdx: index("calendar_events_company").on(table.companyId),
    timeRangeIdx: index("calendar_events_time_range").on(table.companyId, table.startTs, table.endTs),
    statusIdx: index("calendar_events_status").on(table.companyId, table.status),
    providerIdx: index("calendar_events_provider").on(table.companyId, table.provider),
}));
export const calendarNudges = pgTable("calendar_nudges", {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    provider: text("provider").notNull().$type(),
    userId: uuid("user_id").notNull(),
    calendarEventId: uuid("calendar_event_id").references(() => calendarEvents.id, {
        onDelete: "set null",
    }),
    recurringEventId: text("recurring_event_id"),
    nudgeType: text("nudge_type").notNull().$type(),
    deliveredAt: timestamp("delivered_at", { withTimezone: true }).notNull().defaultNow(),
    slackMessageTs: text("slack_message_ts"),
    notificationId: uuid("notification_id"),
    dismissed: boolean("dismissed").notNull().default(false),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, table => ({
    companyUserIdx: index("calendar_nudges_company_user").on(table.companyId, table.userId),
    eventIdx: index("calendar_nudges_event").on(table.calendarEventId),
    deliveredIdx: index("calendar_nudges_delivered").on(table.companyId, table.userId, table.deliveredAt),
    providerIdx: index("calendar_nudges_provider").on(table.companyId, table.provider),
}));
export const calendarUserPreferences = pgTable("calendar_user_preferences", {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    userId: uuid("user_id").notNull(),
    proactivityLevel: text("proactivity_level")
        .notNull()
        .$type()
        .default("normal"),
    maxNudgesPerDay: text("max_nudges_per_day").notNull().default("3"),
    quietHoursStart: text("quiet_hours_start"),
    quietHoursEnd: text("quiet_hours_end"),
    quietHoursTimezone: text("quiet_hours_timezone"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
}, table => ({
    uniqueUserPrefs: uniqueIndex("calendar_user_preferences_company_user").on(table.companyId, table.userId),
}));
// ── Backward-Compatibility Aliases (deprecated) ──────────────────────
/** @deprecated Use calendarSources */
export const gcalCalendars = calendarSources;
/** @deprecated Use calendarEvents */
export const gcalEvents = calendarEvents;
/** @deprecated Use calendarNudges */
export const gcalNudges = calendarNudges;
/** @deprecated Use calendarUserPreferences */
export const gcalUserPreferences = calendarUserPreferences;
